import React, { useState, useEffect, useCallback } from "react";
import { X, Sparkles, Folder } from "lucide-react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { InlineLoadSkeleton } from "./common/InlineLoadSkeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { aiService } from "../services/aiService";
import { projectService } from "../services/projectService";
import sectionService from "../services/sectionService";
import { useModal } from "../context/ModalContext";
import toast from "react-hot-toast";

const DEFAULT_AI_MODES = {
  new: { description: "Generate new test cases.", example: "For new module." },
  regenerate: {
    description: "Regenerate same test cases again.",
    example: "Retry same requirement.",
  },
  more: {
    description: "Generate more test cases beyond existing ones.",
    example: "Extend coverage.",
  },
  update: {
    description: "Generate updated test cases for changed requirements.",
    example: "When logic or validations change.",
  },
};

const AIGenerateTestCasesModal = ({
  isOpen,
  onClose,
  onTestCasesGenerated,
  projectId,
  defaultSectionId,
}) => {
  const { incrementModalCount, decrementModalCount } = useModal();
  const [formData, setFormData] = useState({
    moduleName: "",
    requirements: "",
    mode: "new",
    testCaseCount: 5,
    prdLink: "",
    jiraLinks: "",
    figmaLinks: "",
    sketchLinks: "",
    reviewer_id: "",
    section_id: defaultSectionId || "",
  });

  const [loading, setLoading] = useState(false);
  const [aiModes, setAiModes] = useState({});
  const [loadingModes, setLoadingModes] = useState(false);
  const [projectUsers, setProjectUsers] = useState([]);
  const [loadingProjectUsers, setLoadingProjectUsers] = useState(false);
  const [sections, setSections] = useState([]);
  const [loadingSections, setLoadingSections] = useState(false);

  const fetchAIModes = useCallback(async () => {
    try {
      setLoadingModes(true);
      const response = await aiService.getAIModes();
      if (response?.success && response?.details) {
        setAiModes(response.details);
      } else {
        setAiModes(DEFAULT_AI_MODES);
      }
    } catch (error) {
      console.error("Failed to fetch AI modes:", error);
      setAiModes(DEFAULT_AI_MODES);
    } finally {
      setLoadingModes(false);
    }
  }, []);

  const loadProjectUsers = useCallback(async () => {
    if (!projectId) return;

    try {
      setLoadingProjectUsers(true);
      const response = await projectService.getAssignableUsers(projectId);
      const users = response?.data?.users || response?.users || [];
      setProjectUsers(users);
    } catch (error) {
      console.error("Failed to load assignable users:", error);
      setProjectUsers([]);
    } finally {
      setLoadingProjectUsers(false);
    }
  }, [projectId]);

  const loadSections = useCallback(async () => {
    if (!projectId) return;

    try {
      setLoadingSections(true);
      const response = await sectionService.getSectionTree(projectId);
      const sectionTree = response?.data?.sections || response?.sections || [];
      setSections(sectionTree);
    } catch (error) {
      console.error("Failed to load sections:", error);
      setSections([]);
    } finally {
      setLoadingSections(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (isOpen) {
      // Register modal with context
      incrementModalCount();

      fetchAIModes();
      loadProjectUsers();
      loadSections();
      // Reset section_id to defaultSectionId when modal opens
      if (defaultSectionId) {
        setFormData((prev) => ({ ...prev, section_id: defaultSectionId }));
      }

      return () => {
        // Unregister modal with context when closing
        decrementModalCount();
      };
    }
  }, [
    isOpen,
    defaultSectionId,
    fetchAIModes,
    loadProjectUsers,
    loadSections,
    incrementModalCount,
    decrementModalCount,
  ]);

  // Flatten section tree for dropdown display
  const flattenSections = (sectionList, level = 0) => {
    if (!Array.isArray(sectionList)) {
      return [];
    }

    let result = [];

    sectionList.forEach((section) => {
      if (!section) {
        return;
      }

      const indent = "  ".repeat(level);
      const displayName = section.display_name || section.name;

      result.push({
        section_id: section.section_id,
        name: `${indent}${displayName}`, // Removed emoji icon
        level,
      });

      if (section.children && section.children.length > 0) {
        result = result.concat(flattenSections(section.children, level + 1));
      }
    });

    return result;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const parseLinks = (linksString) => {
    if (!linksString || !linksString.trim()) return [];
    return linksString
      .split("\n")
      .map((link) => link.trim())
      .filter((link) => link.length > 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.moduleName.trim() || !formData.requirements.trim()) {
      toast.error("Please provide module name and requirements");
      return;
    }

    setLoading(true);
    try {
      const response = await aiService.generateTestCases({
        moduleName: formData.moduleName.trim(),
        requirements: formData.requirements.trim(),
        mode: formData.mode,
        testCaseCount: parseInt(formData.testCaseCount) || 5,
        prdLink: formData.prdLink.trim() || undefined,
        jiraLinks: parseLinks(formData.jiraLinks),
        figmaLinks: parseLinks(formData.figmaLinks),
        sketchLinks: parseLinks(formData.sketchLinks),
      });

      if (response.success && response.testCases) {
        toast.success(
          `Successfully generated ${Object.keys(response.testCases).length} test cases`,
        );
        onTestCasesGenerated(response.testCases, {
          moduleName: formData.moduleName,
          sectionId: formData.section_id || defaultSectionId,
          reviewerId:
            formData.reviewer_id && formData.reviewer_id.trim() !== ""
              ? formData.reviewer_id.trim()
              : null,
        });
        handleClose();
      } else {
        toast.error(response.message || "Failed to generate test cases");
      }
    } catch (error) {
      console.error("AI Generation Error:", error);
      toast.error(
        error.response?.data?.message ||
          "Failed to generate test cases. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      moduleName: "",
      requirements: "",
      mode: "new",
      testCaseCount: 5,
      prdLink: "",
      jiraLinks: "",
      figmaLinks: "",
      sketchLinks: "",
      reviewer_id: "",
      section_id: defaultSectionId || "",
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-500/20 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-bg-primary rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border-primary">
          <div className="flex items-center gap-3">
            <Sparkles className="w-6 h-6 text-indigo-600" />
            <h2 className="text-xl font-semibold text-text-primary">
              Generate AI Test Cases
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="text-text-tertiary hover:text-text-secondary transition-colors"
            disabled={loading}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6" autoComplete="off">
          <div className="space-y-6">
            {/* Module Name */}
            <div>
              <Label htmlFor="moduleName">
                Module Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="moduleName"
                name="moduleName"
                value={formData.moduleName}
                onChange={handleInputChange}
                placeholder="e.g., User Authentication, Payment Processing"
                required
                disabled={loading}
              />
            </div>

            {/* Requirements */}
            <div>
              <Label htmlFor="requirements">
                Requirements <span className="text-red-500">*</span>
              </Label>
              <textarea
                id="requirements"
                name="requirements"
                value={formData.requirements}
                onChange={handleInputChange}
                placeholder="Describe the requirements, features, or functionality you want to test..."
                rows={6}
                className="w-full px-4 py-2 bg-background text-foreground border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                required
                disabled={loading}
              />
            </div>

            {/* Mode and Test Case Count */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="mode">Generation Mode</Label>
                <Select
                  value={formData.mode}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, mode: value }))
                  }
                  disabled={loading || loadingModes}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.keys(aiModes).map((mode) => (
                      <SelectItem key={mode} value={mode}>
                        {mode.charAt(0).toUpperCase() + mode.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {aiModes[formData.mode] && (
                  <p className="mt-1 text-xs text-text-tertiary">
                    {aiModes[formData.mode].description}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="testCaseCount">Number of Test Cases</Label>
                <Input
                  id="testCaseCount"
                  name="testCaseCount"
                  type="number"
                  value={formData.testCaseCount}
                  onChange={handleInputChange}
                  min="1"
                  max="20"
                  disabled={loading}
                />
              </div>
            </div>

            {/* PRD Link */}
            <div>
              <Label htmlFor="prdLink">PRD Link (Optional)</Label>
              <Input
                id="prdLink"
                name="prdLink"
                type="url"
                value={formData.prdLink}
                onChange={handleInputChange}
                placeholder="https://..."
                disabled={loading}
              />
            </div>

            {/* Jira Links */}
            <div>
              <Label htmlFor="jiraLinks">Jira Ticket Links (Optional)</Label>
              <textarea
                id="jiraLinks"
                name="jiraLinks"
                value={formData.jiraLinks}
                onChange={handleInputChange}
                placeholder="Enter one Jira link per line"
                rows={3}
                className="w-full px-4 py-2 bg-background text-foreground border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                disabled={loading}
              />
            </div>

            {/* Figma Links */}
            <div>
              <Label htmlFor="figmaLinks">Figma Design Links (Optional)</Label>
              <textarea
                id="figmaLinks"
                name="figmaLinks"
                value={formData.figmaLinks}
                onChange={handleInputChange}
                placeholder="Enter one Figma link per line"
                rows={3}
                className="w-full px-4 py-2 bg-background text-foreground border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                disabled={loading}
              />
            </div>

            {/* Sketch Links */}
            <div>
              <Label htmlFor="sketchLinks">
                Sketch Design Links (Optional)
              </Label>
              <textarea
                id="sketchLinks"
                name="sketchLinks"
                value={formData.sketchLinks}
                onChange={handleInputChange}
                placeholder="Enter one Sketch link per line"
                rows={3}
                className="w-full px-4 py-2 bg-background text-foreground border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                disabled={loading}
              />
            </div>

            {/* Section Selection */}
            <div>
              <Label htmlFor="section_id">Section (Optional)</Label>
              <Select
                value={formData.section_id || "root"}
                onValueChange={(value) =>
                  setFormData((prev) => ({
                    ...prev,
                    section_id: value === "root" ? "" : value,
                  }))
                }
                disabled={loading || loadingSections}
              >
                <SelectTrigger>
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <SelectValue placeholder="Root Section" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="root">
                    <div className="flex items-center gap-2">
                      <Folder className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                      <span>Root Section</span>
                    </div>
                  </SelectItem>
                  {flattenSections(sections).map((section) => (
                    <SelectItem
                      key={section.section_id}
                      value={section.section_id}
                    >
                      <div className="flex items-center gap-2">
                        <Folder className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                        <span>{section.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Reviewer Selection */}
            <div>
              <Label htmlFor="reviewer_id">Reviewer (Optional)</Label>
              <Select
                value={formData.reviewer_id || "none"}
                onValueChange={(value) =>
                  setFormData((prev) => ({
                    ...prev,
                    reviewer_id: value === "none" ? "" : value,
                  }))
                }
                disabled={loading || loadingProjectUsers}
              >
                <SelectTrigger>
                  <SelectValue placeholder="No Reviewer" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Reviewer</SelectItem>
                  {projectUsers.map((user) => (
                    <SelectItem key={user.user_id} value={user.user_id}>
                      {user.full_name || user.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-1 text-xs text-text-tertiary">
                An email notification will be sent to the reviewer when test
                cases are created
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 mt-6 pt-6 border-t border-border-primary">
            <Button
              type="button"
              onClick={handleClose}
              variant="outline"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <InlineLoadSkeleton className="h-4 w-4 mr-2" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate Test Cases
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AIGenerateTestCasesModal;
